/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';

/**
 * Komunikátor čísel pro Ki-Wi Server.
 */
class CommunicatorNumber {
  /**
   * Načte číslo.
   * @param {number | null | undefined} value Číslo ve formátu, který vrací server.
   * @returns {number | null} Číslo.
   */
  static fromNumberOrNull(value) {
    if (isNil(value)) {
      return null;
    }
    if (!isNumber(value)) {
      throw new TypeError(`Invalid number ${JSON.stringify(value)}.`);
    }
    return value;
  }

  /**
   * Načte číslo, vrací vždy `number`, nebo vyhodí chybu.
   * @param {number} value Číslo ve formátu, který vrací server.
   * @returns {number} Číslo.
   */
  static fromNumberRequired(value) {
    if (isNil(value)) {
      throw new TypeError(`Missing number ${JSON.stringify(value)}.`);
    }
    if (!isNumber(value)) {
      throw new TypeError(`Invalid number ${JSON.stringify(value)}.`);
    }
    return value;
  }

  /**
   * Načte číslo z řetězce.
   * @param {string | null | undefined} value Číslo jako řetězec ve formátu, který vrací server.
   * @returns {number | null} Číslo.
   */
  static fromStringOrNull(value) {
    if (isNil(value)) {
      return null;
    }
    if (!isString(value)) {
      throw new TypeError(`Invalid string ${JSON.stringify(value)}.`);
    }
    // Odebrání okolního whitespace.
    const trimmed = value.trim();
    // Prázdný string se parsuje jako null, nikoliv jako 0 v algoritmu Number().
    if (!trimmed.length) {
      return null;
    }
    // Detekce speciálního zápisu čísel: binary, octal, hexadecimal, exponentiation.
    if (/b|e|o|x/i.test(trimmed)) {
      return NaN;
    }
    return Number(trimmed);
  }

  /**
   * Načte číslo z řetězce, vrací vždy `number`, nebo vyhodí chybu.
   * @param {string} value Číslo jako řetězec ve formátu, který vrací server.
   * @returns {number} Číslo.
   */
  static fromStringRequired(value) {
    if (isNil(value)) {
      throw new TypeError(`Missing string ${JSON.stringify(value)}.`);
    }
    if (!isString(value)) {
      throw new TypeError(`Invalid string ${JSON.stringify(value)}.`);
    }
    // Odebrání okolního whitespace.
    const trimmed = value.trim();
    // Prázdný string se parsuje jako null, nikoliv jako 0 v algoritmu Number().
    if (!trimmed.length) {
      throw new TypeError(
        `Empty or whitespace-only string ${JSON.stringify(value)}.`
      );
    }
    // Detekce speciálního zápisu čísel: binary, octal, hexadecimal, exponentiation.
    if (/b|e|o|x/i.test(trimmed)) {
      return NaN;
    }
    return Number(trimmed);
  }
}

export default CommunicatorNumber;
